import React from 'react';
import { Calendar, Globe2, Wallet, MapPin, Plane, Clock } from 'lucide-react';
import { TravelProfile } from '../../../types/matching';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { budgetCountries } from '../../../data/budget-countries';
import { translateTravelStyle } from '../../../utils/matching';

interface ProfilePreferencesProps {
  profile: TravelProfile;
}

export function ProfilePreferences({ profile }: ProfilePreferencesProps) {
  const destinationInfo = React.useMemo(() => {
    if (!profile.preferences.destination) return { flag: null, name: "destination inconnue" };
    const country = budgetCountries.find(c => c.code === profile.preferences.destination);
    return {
      flag: country?.flag || null,
      name: country?.name || "destination inconnue"
    };
  }, [profile.preferences.destination]);

  const duration = Math.ceil((
    (profile.preferences.endDate.toDate?.() || new Date(profile.preferences.endDate)).getTime() -
    (profile.preferences.startDate.toDate?.() || new Date(profile.preferences.startDate)).getTime()
  ) / (1000 * 60 * 60 * 24 * 30));

  const items = [
    {
      icon: MapPin,
      label: 'Destination',
      value: (
        <span className="flex items-center gap-2">
          {profile.preferences.city}
          {destinationInfo.flag && <span className="text-xl">{destinationInfo.flag}</span>}
        </span>
      ),
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    },
    {
      icon: Globe2,
      label: 'Style de voyage',
      value: translateTravelStyle(profile.preferences.style),
      iconColor: 'text-teal-500',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
    },
    {
      icon: Calendar,
      label: 'Depart prevu',
      value: (
        <span>
          {format(
            profile.preferences.startDate.toDate?.() || new Date(profile.preferences.startDate),
            'MMMM yyyy',
            { locale: fr }
          )}
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-1.5">
            +/- {profile.preferences.flexibility}j
          </span>
        </span>
      ),
      iconColor: 'text-emerald-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    },
    {
      icon: Clock,
      label: 'Duree prevue',
      value: `${duration} mois`,
      iconColor: 'text-amber-500',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    },
    {
      icon: Wallet,
      label: 'Budget mensuel',
      value: `${profile.preferences.budget.min.toLocaleString('fr-FR')} - ${profile.preferences.budget.max.toLocaleString('fr-FR')} EUR`,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <Plane className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Projet de voyage</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className={`flex items-start gap-3.5 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50 ${item.label === 'Budget mensuel' ? 'sm:col-span-2' : ''}`}
            >
              <div className={`p-2 rounded-lg ${item.bgColor} flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${item.iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-0.5">{item.label}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{item.value}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
